import { useEffect, useState, useRef } from "react";

/** Un color per bloc sencer (tots els botons, barra i rellotge de franja comparteixen aquest to). */
type BlockTone =
    | "dawn"
    | "work"
    | "home"
    | "study"
    | "build"
    | "news"
    | "play"
    | "meal"
    | "stream"
    | "sleep";

interface Link {
    label: string;
    url: string;
    noLink?: boolean;
    plainText?: boolean;
}

interface ScheduleBlock {
    time: string;
    title: string;
    tone: BlockTone;
    links: Link[];
    centeredOnly?: boolean;
}

const SCHEDULE: ScheduleBlock[] = [
    {
        time: "9:00 / 10:00",
        title: "matí",
        tone: "dawn",
        links: [
            { label: "rentaplats", url: "#", noLink: true },
            { label: "parar taula", url: "#", noLink: true },
            { label: "esmorzar + yt", url: "#", noLink: true },
        ]
    },
    {
        time: "10:00 / 11:30",
        title: "deures / estudiar",
        tone: "study",
        links: [
            { label: "Euncet", url: "https://campus.myeuncet.com/class" },
        ]
    },
    {
        time: "11:30 / 12:00",
        title: "dutxar",
        tone: "home",
        links: [],
        centeredOnly: true,
    },
    {
        time: "12:00 / 13:00",
        title: "RAC1 La Competència",
        tone: "news",
        links: [
            { label: "RAC1 La Competència", url: "https://www.rac1.cat/directe" },
        ]
    },
    {
        time: "13:00 / 13:30",
        title: "llegir",
        tone: "news",
        links: [
            { label: "WSJ", url: "https://customercenter.wsj.com/todaysPaper/" },
            { label: "Blinkist", url: "https://www.blinkist.com/en/app/for-you" },
            { label: "3CatInfo", url: "https://www.3cat.cat/3catinfo/" },
            { label: "La Vanguardia", url: "https://www-lavanguardia-com.translate.goog/?_x_tr_sl=es&_x_tr_tl=ca&_x_tr_hl=ca&_x_tr_pto=sc" },
            { label: "Via Empresa", url: "https://www.viaempresa.cat/" },
            { label: "Starter Story", url: "https://www.starterstory.com/explore" },
        ]
    },
    {
        time: "13:30 / 14:30",
        title: "projectes",
        tone: "build",
        links: [
            { label: "projectes", url: "#", noLink: true },
        ]
    },
    {
        time: "14:30 / 17:30",
        title: "dinar",
        tone: "meal",
        links: [],
        centeredOnly: true,
    },
    {
        time: "14:30 / 17:30",
        title: "si tinc uni = preparar-se",
        tone: "meal",
        links: [],
        centeredOnly: true,
    },
    {
        time: "17:30 / 18:45",
        title: "projectes + animations.dev",
        tone: "build",
        links: [
            { label: "projectes", url: "#", noLink: true },
            { label: "Dominis", url: "#", noLink: true },
            { label: "animations.dev", url: "https://animations.dev/learn" },
        ]
    },
    {
        time: "18:45 / 20:00",
        title: "deures / estudiar",
        tone: "study",
        links: [
            { label: "Euncet", url: "https://campus.myeuncet.com/class" },
        ]
    },
    {
        time: "20:00 / 20:30",
        title: "PS5 + TV",
        tone: "stream",
        links: [
            { label: "PS5 (forza wheelspin)", url: "#", noLink: true },
            { label: "YT", url: "https://www.youtube.com/playlist?list=WL" },
            { label: "Apple TV", url: "https://tv.apple.com/" },
            { label: "HBO", url: "https://www.max.com/" },
            { label: "Netflix", url: "https://www.netflix.com/" },
        ]
    },
    {
        time: "20:30 / 21:00",
        title: "llegir",
        tone: "news",
        links: [
            { label: "WSJ", url: "https://customercenter.wsj.com/todaysPaper/" },
            { label: "Blinkist", url: "https://www.blinkist.com/en/app/for-you" },
            { label: "3CatInfo", url: "https://www.3cat.cat/3catinfo/" },
            { label: "La Vanguardia", url: "https://www-lavanguardia-com.translate.goog/?_x_tr_sl=es&_x_tr_tl=ca&_x_tr_hl=ca&_x_tr_pto=sc" },
            { label: "Via Empresa", url: "https://www.viaempresa.cat/" },
            { label: "Starter Story", url: "https://www.starterstory.com/explore" },
        ]
    },
    {
        time: "21:00 / 24:00",
        title: "sopar + anar a dormir (1am)",
        tone: "meal",
        links: [],
        centeredOnly: true,
    },
];

const COLORS: Record<BlockTone, string> = {
    dawn: "#d97706",
    work: "#2563eb",
    home: "#0d9488",
    study: "#15803d",
    build: "#db2777",
    news: "#ea580c",
    play: "#9333ea",
    meal: "#b45309",
    stream: "#dc2626",
    sleep: "#4338ca",
};

function isCurrentTime(timeRange: string): boolean {
    if (!timeRange) return false;
    
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTime = currentHour * 60 + currentMinute;
    
    const times = timeRange.split(' / ');
    
    if (times.length === 2) {
        const [startHour, startMin] = times[0].split(':').map(Number);
        const [endHour, endMin] = times[1].split(':').map(Number);
        const startTime = startHour * 60 + startMin;
        const endTime = endHour * 60 + endMin;
        return currentTime >= startTime && currentTime < endTime;
    }
    
    return false;
}

function getTimeRemaining(timeRange: string): { minutes: number; seconds: number; total: number } {
    if (!timeRange) return { minutes: 0, seconds: 0, total: 0 };
    
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentSecond = now.getSeconds();
    const currentTime = currentHour * 3600 + currentMinute * 60 + currentSecond;
    
    const times = timeRange.split(' / ');
    
    if (times.length === 2) {
        const [endHour, endMin] = times[1].split(':').map(Number);
        const endTime = endHour * 3600 + endMin * 60;
        const remainingSeconds = Math.max(0, endTime - currentTime);
        const minutes = Math.floor(remainingSeconds / 60);
        const seconds = remainingSeconds % 60;
        return { minutes, seconds, total: remainingSeconds };
    }
    
    return { minutes: 0, seconds: 0, total: 0 };
}

function getProgressPercentage(timeRange: string): number {
    if (!timeRange) return 0;
    
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentSecond = now.getSeconds();
    const currentTime = currentHour * 3600 + currentMinute * 60 + currentSecond;
    
    const times = timeRange.split(' / ');
    
    if (times.length === 2) {
        const [startHour, startMin] = times[0].split(':').map(Number);
        const [endHour, endMin] = times[1].split(':').map(Number);
        const startTime = startHour * 3600 + startMin * 60;
        const endTime = endHour * 3600 + endMin * 60;
        const duration = endTime - startTime;
        const elapsed = currentTime - startTime;
        return Math.min(100, Math.max(0, (elapsed / duration) * 100));
    }
    
    return 0;
}

export function Horari() {
    const [currentTime, setCurrentTime] = useState(new Date());
    const activeCardRef = useRef<HTMLDivElement>(null);
    const hasScrolled = useRef(false);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (activeCardRef.current && !hasScrolled.current) {
            setTimeout(() => {
                activeCardRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
                hasScrolled.current = true;
            }, 100);
        }
    }, []);

    return (
        <div className="min-h-screen bg-black text-white p-8">
            <div className="max-w-2xl mx-auto">
                {/* Header with clock */}
                <div className="mb-8 flex justify-between items-center">
                    <h1 className="text-4xl font-bold">🕑</h1>
                    <div className="text-2xl font-semibold">
                        {currentTime.toLocaleTimeString('ca-ES', { hour12: false })}
                    </div>
                </div>

                {/* Timeline */}
                <div className="space-y-6">
                    {SCHEDULE.map((block, index) => {
                        if (block.centeredOnly) {
                            return (
                                <div key={index} className="text-center py-4 text-lg font-semibold text-gray-200">
                                    {block.title}
                                </div>
                            );
                        }

                        // Title-only entries (no time and no links)
                        if (!block.time && block.links.length === 0) {
                            return (
                                <div key={index} className="text-center py-4 text-lg font-semibold text-gray-400">
                                    {block.title}
                                </div>
                            );
                        }

                        const isActive = isCurrentTime(block.time);
                        const remaining = isActive ? getTimeRemaining(block.time) : null;
                        const progress = isActive ? getProgressPercentage(block.time) : 0;

                        return (
                            <div
                                key={index}
                                ref={isActive ? activeCardRef : null}
                                className="rounded-2xl p-6 transition-all"
                                style={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                    border: isActive
                                        ? `2px solid ${COLORS[block.tone]}`
                                        : '1px solid rgba(255, 255, 255, 0.1)',
                                    boxShadow: isActive ? `0 0 0 1px ${COLORS[block.tone]}33` : undefined,
                                }}
                            >
                                {/* Header */}
                                <div className={`flex items-center justify-between ${block.links.length > 0 ? 'mb-4' : ''}`}>
                                    <div className="text-xl font-bold">{block.title}</div>
                                    {block.time && (
                                        <div
                                            className="text-sm font-medium tabular-nums"
                                            style={{ color: COLORS[block.tone] }}
                                        >
                                            {block.time}
                                        </div>
                                    )}
                                </div>

                                {/* Progress bar for active block */}
                                {isActive && remaining && (
                                    <>
                                        <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mb-4">
                                            <div
                                                className="h-full transition-all duration-1000 rounded-full"
                                                style={{
                                                    width: `${progress}%`,
                                                    backgroundColor: COLORS[block.tone],
                                                }}
                                            />
                                        </div>
                                        <div className="text-xs text-gray-400 font-medium mb-4">
                                            ⏱️ {remaining.minutes}:{remaining.seconds.toString().padStart(2, '0')} restants
                                        </div>
                                    </>
                                )}

                                {/* Links */}
                                <div className="space-y-2">
                                    {block.links.map((link, linkIndex) => {
                                        if (link.noLink) {
                                            if (link.plainText) {
                                                return (
                                                    <div
                                                        key={linkIndex}
                                                        className="w-full py-2 text-center font-semibold text-white"
                                                    >
                                                        {link.label}
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div
                                                    key={linkIndex}
                                                    className="w-full py-3 px-4 text-center rounded-xl font-semibold text-white"
                                                    style={{ backgroundColor: COLORS[block.tone] }}
                                                >
                                                    {link.label}
                                                </div>
                                            );
                                        }
                                        
                                        return (
                                            <a
                                                key={linkIndex}
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="block w-full py-3 px-4 text-center rounded-xl font-semibold text-white hover:opacity-90 transition-opacity"
                                                style={{ backgroundColor: COLORS[block.tone] }}
                                            >
                                                {link.label}
                                            </a>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
